<!-- src/components/device/DeviceMap.vue -->
<template>
  <div class="bg-white rounded-lg shadow p-4">
    <div class="flex justify-between items-start mb-3">
      <div>
        <h3 class="font-semibold text-gray-800">{{ $t('carteGps.titre') }}</h3>
        <p v-if="position" class="text-xs text-gray-400 mt-0.5">
          {{ srcLabel }} — {{ formatDate(position.timestamp) }}
          <span v-if="position.sat" class="ml-2">{{ $t('carteGps.satellites', { n: position.sat }) }}</span>
          <span v-if="position.fix === 0" class="ml-2 text-yellow-600">{{ $t('carteGps.fixNonAcquis') }}</span>
        </p>
        <p v-else class="text-xs text-gray-400 mt-0.5">{{ $t('carteGps.aucune') }}</p>
      </div>
      <a v-if="position" :href="googleMapsUrl" target="_blank" rel="noopener"
         class="flex-shrink-0 px-3 py-1 rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-medium transition-colors">
        {{ $t('carteGps.ouvrirMaps') }}
      </a>
    </div>

    <div v-if="position" ref="mapContainer" class="rounded-lg overflow-hidden z-0" style="height: 320px;"></div>
    <div v-else class="flex items-center justify-center rounded-lg bg-gray-50 border border-gray-200 text-gray-400 text-sm" style="height: 200px;">
      {{ $t('carteGps.attente') }}
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { localeDateFns, enParis } from '@/i18n/dates';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix icône Leaflet + Webpack
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

export default defineComponent({
  name: 'DeviceMap',

  props: {
    position: {
      type: Object,
      default: null
      // { lat, lon, fix, sat, src, timestamp }
    }
  },

  setup(props) {
    const { t } = useI18n();
    const mapContainer = ref(null);
    let map = null;
    let marker = null;

    const googleMapsUrl = computed(() => {
      if (!props.position) return '#';
      return `https://www.google.com/maps?q=${props.position.lat},${props.position.lon}`;
    });

    const srcLabel = computed(() => {
      const src = props.position?.src;
      if (src === 'accel_gnss') return t('carteGps.srcAccel');
      if (src === 'guard_gnss') return t('carteGps.srcGuard');
      return src || t('carteGps.srcDefaut');
    });

    const formatDate = (d) => {
      if (!d) return '';
      try { return format(enParis(parseISO(d)), t('carteGps.formatDate'), { locale: localeDateFns() }); }
      catch { return d; }
    };

    const initMap = () => {
      if (!props.position || !mapContainer.value || map) return;
      map = L.map(mapContainer.value).setView([props.position.lat, props.position.lon], 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);
      marker = L.marker([props.position.lat, props.position.lon]).addTo(map);
    };

    const updateMarker = () => {
      if (!map || !marker || !props.position) return;
      const latlng = [props.position.lat, props.position.lon];
      marker.setLatLng(latlng);
      map.setView(latlng, map.getZoom());
    };

    onMounted(async () => {
      if (props.position) {
        await nextTick();
        initMap();
      }
    });

    watch(() => props.position, async (newPos) => {
      if (!newPos) return;
      await nextTick();
      if (!map) {
        initMap();
      } else {
        updateMarker();
      }
    }, { deep: true });

    onBeforeUnmount(() => {
      if (map) { map.remove(); map = null; marker = null; }
    });

    return { mapContainer, googleMapsUrl, srcLabel, formatDate };
  }
});
</script>
